import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { formatInTz } from '../lib/utcClock.js';
import { DEFAULT_TIMEZONE } from '../lib/timezones.js';
import { HeaderActions } from '../lib/pageHeader.jsx';

// Single aggregation call — see server/src/routes/dashboard.js. Anything
// the Dashboard needs lives in this payload; don't bolt on separate fetches
// per card without a reason (latency, round-trip amplification).
export default function Dashboard() {
  const { user } = useAuth();
  const tz = user?.timezone || DEFAULT_TIMEZONE;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);

  async function load() {
    setError('');
    setRefreshing(true);
    try {
      const r = await api.get('/dashboard');
      setData(r);
      setRefreshedAt(new Date());
    } catch (e) {
      setError(e?.data?.error ?? e.message ?? 'Failed to load');
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <HeaderActions>
        {refreshedAt && (
          <span className="hidden font-mono text-xs text-ink-500 md:inline">
            Refreshed {formatInTz(refreshedAt, tz)}
          </span>
        )}
        <button
          className="btn-ghost"
          onClick={load}
          disabled={refreshing}
          title="Reload stats"
        >
          <i className={`fa-solid fa-arrows-rotate ${refreshing ? 'fa-spin-pulse' : ''}`} />
          <span className="ml-2">Refresh</span>
        </button>
      </HeaderActions>

      <div>
        <p className="text-sm text-ink-500">System-wide overview — counts, health, and recent activity.</p>
      </div>

      {error && (
        <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <i className="fa-solid fa-triangle-exclamation mr-2" />
          {error}
        </div>
      )}

      {!data && !error && (
        <div className="card p-8 text-center text-sm text-ink-500">
          <i className="fa-solid fa-spinner fa-spin-pulse mr-2" />
          Loading…
        </div>
      )}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <DatasetsCard data={data.datasets} />
            <ApiKeysCard  data={data.api_keys} />
            <UsersCard    data={data.users} />
            <SystemCard   data={data.system} tz={tz} />
          </div>

          <RecentActivity items={data.recent_activity} tz={tz} />
        </>
      )}
    </div>
  );
}

// ── Shared building blocks ────────────────────────────────────────────────

function StatCard({ icon, iconTone, title, subtitle, to, children }) {
  return (
    <section className="card flex flex-col p-5">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconTone}`}
        >
          <i className={`fa-solid ${icon} text-lg`} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-500">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-ink-500">{subtitle}</p>}
        </div>
        {to && (
          <NavLink
            to={to}
            className="text-ink-400 transition hover:text-ink-700"
            title="Open"
          >
            <i className="fa-solid fa-arrow-right" />
          </NavLink>
        )}
      </div>
      <div className="mt-4 flex-1 space-y-3">{children}</div>
    </section>
  );
}

function BigNumber({ value, label, tone = 'text-ink-900' }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className={`font-mono text-3xl font-bold ${tone}`}>{value}</div>
      {label && <div className="text-xs text-ink-500">{label}</div>}
    </div>
  );
}

function Row({ icon, label, children, tone = 'text-ink-800' }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="inline-flex items-center gap-2 text-ink-500">
        {icon && <i className={`fa-solid ${icon} w-3.5 text-ink-400`} />}
        {label}
      </span>
      <span className={`font-mono font-semibold ${tone}`}>{children}</span>
    </div>
  );
}

function Chip({ children, tone = 'bg-ink-100 text-ink-700' }) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
      {children}
    </span>
  );
}

// ── Datasets ──────────────────────────────────────────────────────────────

function DatasetsCard({ data }) {
  const hasMissing = data.missing > 0;
  return (
    <StatCard
      icon="fa-layer-group"
      iconTone="bg-indigo-50 text-indigo-600"
      title="Datasets"
      subtitle="Tile stores & services"
      to="/datasets"
    >
      <BigNumber value={data.total} label={data.total === 1 ? 'dataset' : 'datasets'} />

      <div className="space-y-1.5">
        <Row icon="fa-circle-check" label="Available" tone="text-emerald-700">{data.available}</Row>
        <Row
          icon="fa-circle-xmark"
          label="Missing"
          tone={hasMissing ? 'text-red-700' : 'text-ink-400'}
        >
          {data.missing}
        </Row>
        <Row icon="fa-hard-drive" label="Total size">
          {data.total_size_gb != null ? `${data.total_size_gb} GB` : '—'}
        </Row>
      </div>

      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-400">Endpoints</div>
        <div className="flex flex-wrap gap-1">
          {data.endpoints.map((e) => (
            <Chip key={e} tone={ENDPOINT_TONE[e] ?? 'bg-ink-100 text-ink-700'}>{e}</Chip>
          ))}
        </div>
      </div>
    </StatCard>
  );
}

const ENDPOINT_TONE = {
  planet:    'bg-emerald-50 text-emerald-700',
  terrain:   'bg-amber-50 text-amber-700',
  satellite: 'bg-indigo-50 text-indigo-700',
  elevation: 'bg-sky-50 text-sky-700',
  editor:    'bg-fuchsia-50 text-fuchsia-700'
};

// ── API Keys ──────────────────────────────────────────────────────────────

function ApiKeysCard({ data }) {
  const u = data.usage_7d ?? { ok: 0, deny: 0, rate: 0, total: 0, today: 0 };
  return (
    <StatCard
      icon="fa-key"
      iconTone="bg-amber-50 text-amber-600"
      title="API Keys"
      subtitle="Gate to tile endpoints"
      to="/api-keys"
    >
      <BigNumber value={data.active} label={`of ${data.total} active`} tone="text-amber-700" />

      <div className="space-y-1.5">
        <Row icon="fa-circle-check" label="Active" tone="text-emerald-700">{data.active}</Row>
        <Row icon="fa-pause" label="Inactive" tone={data.inactive ? 'text-ink-800' : 'text-ink-400'}>
          {data.inactive}
        </Row>
        <Row
          icon="fa-hourglass-end"
          label="Expired"
          tone={data.expired ? 'text-red-700' : 'text-ink-400'}
        >
          {data.expired}
        </Row>
      </div>

      <div className="rounded-md bg-ink-50 p-3">
        <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-ink-500">
          <span>Usage · 7d</span>
          <span className="font-mono text-ink-700">Σ {fmtNum(u.total)}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <UsageTile tone="emerald" label="OK"   value={u.ok} />
          <UsageTile tone="red"     label="Deny" value={u.deny} />
          <UsageTile tone="amber"   label="Rate" value={u.rate} />
        </div>
        <div className="mt-2 text-[10px] text-ink-500">
          Today: <span className="font-mono text-ink-700">{fmtNum(u.today)}</span>
        </div>
      </div>
    </StatCard>
  );
}

function UsageTile({ tone, label, value }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700',
    red:     'bg-red-50 text-red-700',
    amber:   'bg-amber-50 text-amber-700'
  };
  return (
    <div className={`rounded px-2 py-1.5 ${tones[tone]}`}>
      <div className="font-mono text-sm font-bold">{fmtNum(value)}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}

// ── Users ─────────────────────────────────────────────────────────────────

function UsersCard({ data }) {
  return (
    <StatCard
      icon="fa-users"
      iconTone="bg-emerald-50 text-emerald-600"
      title="Users"
      subtitle="Accounts & sessions"
      to="/users"
    >
      <BigNumber value={data.total} label={data.total === 1 ? 'account' : 'accounts'} tone="text-emerald-700" />

      <div className="space-y-1.5">
        <Row icon="fa-user-shield" label="Admins">{data.admins}</Row>
        <Row icon="fa-user"        label="Regular users">{data.regulars}</Row>
        <Row icon="fa-circle" label="Online now" tone={data.active_sessions ? 'text-emerald-700' : 'text-ink-400'}>
          {data.active_users} <span className="text-ink-400">/ {data.active_sessions}</span>
        </Row>
      </div>

      <div className="rounded-md bg-ink-50 p-3">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
          Auth · last 24 h
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="inline-flex items-center gap-1.5 text-emerald-700">
            <i className="fa-solid fa-right-to-bracket" /> Successful
          </span>
          <span className="font-mono font-bold text-emerald-700">{fmtNum(data.logins_ok_24h)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-sm">
          <span className={`inline-flex items-center gap-1.5 ${data.logins_fail_24h ? 'text-red-700' : 'text-ink-400'}`}>
            <i className="fa-solid fa-triangle-exclamation" /> Failed
          </span>
          <span className={`font-mono font-bold ${data.logins_fail_24h ? 'text-red-700' : 'text-ink-400'}`}>
            {fmtNum(data.logins_fail_24h)}
          </span>
        </div>
      </div>
    </StatCard>
  );
}

// ── System ────────────────────────────────────────────────────────────────

function SystemCard({ data, tz }) {
  const ok = data.status === 'ok';
  return (
    <StatCard
      icon="fa-server"
      iconTone="bg-slate-100 text-slate-700"
      title="System"
      subtitle="Runtime & database"
    >
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}
        >
          <i className={`fa-solid fa-circle text-[6px] ${ok ? 'text-emerald-500' : 'text-red-500'}`} />
          {data.status}
        </span>
        <span className="font-mono text-xs text-ink-500">{data.env}</span>
      </div>

      <div className="space-y-1.5">
        <Row icon="fa-code-branch" label="Version">
          <span className="text-xs">{data.version}</span>
        </Row>
        <Row icon="fa-node-js" label="Node">
          <span className="text-xs">{data.node}</span>
        </Row>
        <Row icon="fa-stopwatch" label="Uptime">{humanUptime(data.uptime_s)}</Row>
        <Row icon="fa-power-off" label="Started">
          <span className="text-[11px]">{formatInTz(data.started_at, tz)}</span>
        </Row>
      </div>

      <div className="rounded-md bg-ink-50 p-3">
        <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-ink-500">
          <span><i className="fa-solid fa-database mr-1" />Database</span>
          <span className={data.db.ok ? 'text-emerald-600' : 'text-red-600'}>
            {data.db.ok ? 'healthy' : 'down'}
          </span>
        </div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-ink-500">Size</span>
            <span className="font-mono font-semibold text-ink-800">
              {data.db.size_mb != null ? `${data.db.size_mb} MB` : '—'}
            </span>
          </div>
          <div className="truncate font-mono text-[10px] text-ink-500" title={data.db.path}>
            {data.db.path}
          </div>
        </div>
      </div>
    </StatCard>
  );
}

// ── Recent activity ───────────────────────────────────────────────────────

const ACTION_TONE = {
  'auth.login.success':   'bg-emerald-50 text-emerald-700',
  'auth.login.fail':      'bg-red-50 text-red-700',
  'auth.logout':          'bg-ink-100 text-ink-700',
  'user.create':          'bg-sky-50 text-sky-700',
  'user.update':          'bg-amber-50 text-amber-700',
  'user.delete':          'bg-red-50 text-red-700',
  'user.password.change': 'bg-fuchsia-50 text-fuchsia-700',
  'user.password.reset':  'bg-fuchsia-50 text-fuchsia-700',
  'user.prefs.update':    'bg-ink-100 text-ink-700',
  'apikey.create':        'bg-sky-50 text-sky-700',
  'apikey.update':        'bg-amber-50 text-amber-700',
  'apikey.regenerate':    'bg-orange-50 text-orange-700',
  'apikey.toggle':        'bg-indigo-50 text-indigo-700',
  'apikey.delete':        'bg-red-50 text-red-700',
  'elevation.refresh':    'bg-sky-50 text-sky-700'
};

function ActionChip({ action }) {
  const tone = ACTION_TONE[action] ?? 'bg-ink-100 text-ink-700';
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[11px] ${tone}`}>
      {action}
    </span>
  );
}

function RecentActivity({ items, tz }) {
  if (!items || items.length === 0) {
    return (
      <section className="card p-5">
        <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-ink-500">
          <i className="fa-solid fa-clock-rotate-left mr-2 text-ink-400" />Recent activity
        </h3>
        <div className="py-4 text-center text-sm text-ink-500">No recorded activity yet.</div>
      </section>
    );
  }
  return (
    <section className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-500">
          <i className="fa-solid fa-clock-rotate-left mr-2 text-ink-400" />Recent activity
        </h3>
        <NavLink to="/logs" className="text-xs text-brand-600 hover:underline">
          View all logs <i className="fa-solid fa-arrow-right text-[10px]" />
        </NavLink>
      </div>

      <ul className="divide-y divide-ink-100">
        {items.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <ActionChip action={row.action} />
            <span className="text-ink-700">
              {row.user_username
                ? <><span className="font-semibold">{row.user_username}</span> <span className="text-ink-400">#{row.user_id}</span></>
                : <em className="text-ink-400">system</em>}
            </span>
            {row.target_type && (
              <span className="font-mono text-xs text-ink-500">
                → {row.target_type}{row.target_id && <span className="text-ink-400"> #{row.target_id}</span>}
              </span>
            )}
            {row.ip && <span className="font-mono text-[11px] text-ink-400">{row.ip}</span>}
            <span className="ml-auto whitespace-nowrap font-mono text-[11px] text-ink-500">
              {formatInTz(row.at, tz)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtNum(n) {
  if (n == null) return '—';
  const v = Number(n) | 0;
  return v.toLocaleString('en-US');
}

function humanUptime(sec) {
  const s = Math.max(0, sec | 0);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (d) return `${d}d ${h}h ${m}m`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${r}s`;
  return `${r}s`;
}
